import { useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import {
  Department,
  GetAllDepartmentResponse,
  GetGetIncidentsInjuriesResponse,
  IncidentsSeverityEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentStatusEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
} from '@EHSNavigator/backend';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../components/Loader';
import InjuriesHub from '../../views/InjuriesHub';

export default function InjuriesHubPage() {
  const { DepartmentApi, InvestigationReportingApi } = useApiClients();
  const [searchParams] = useSearchParams();
  const currentPage = searchParams.get('page') ?? 1;
  const searchQuery = searchParams.get('search') ?? undefined;
  const type = searchParams.get('type') ?? null;
  const classification = searchParams.get('classification') ?? null;
  const typeOfLoss = searchParams.get('typeOfLoss') ?? null;
  const department = searchParams.get('department') ?? undefined;
  const status = searchParams.get('status') ?? null;
  const severity = searchParams.get('severity') ?? '';
  const limit = searchParams.get('limit') ?? 10;

  const query: any = {
    page: Number(currentPage) || 1,
  };

  if (searchQuery) query.search = searchQuery;
  if (department) query.departmentId = department;
  if (severity) query.severity = severity as IncidentsSeverityEnum;
  if (status)
    query.status =
      status as InvestigationReportingControllerFindAllInjuriesIncidentStatusEnum;
  if (type) query.type = type;
  if (classification)
    query.classification =
      classification as InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum;
  if (typeOfLoss)
    query.typeOfLoss =
      typeOfLoss as InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum;
  if (limit) query.limit = Number(limit);

  const {
    data: allInjuries,
    isLoading,
    isRefetching,
  } = useQuery<GetGetIncidentsInjuriesResponse, Error, any>({
    queryKey: [
      'all-incidents',
      searchQuery,
      currentPage,
      department,
      severity,
      type,
      classification,
      typeOfLoss,
      Number(limit),

      status,
    ],
    queryFn: async () => {
      const response =
        await InvestigationReportingApi?.investigationReportingControllerFindAllInjuriesIncident(
          query,
        );
      return response?.data || [];
    },
  });

  const { data: allDepartments, isLoading: departmentsLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['my-reported-departments'],
      queryFn: async () => {
        const response =
          await DepartmentApi.departmentControllerFindAllDepartment({
            disablePagination: true,
          });
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const hasFilters =
    !!searchQuery || !!department || !!status || !!type || !!classification;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : "You haven't reported an incident yet";

  if (departmentsLoading) {
    return <Loader />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <InjuriesHub
        params={{
          search: searchQuery,
          page: Number(currentPage),
          departmentId: department,
          severity: severity as IncidentsSeverityEnum,
          status:
            status as InvestigationReportingControllerFindAllInjuriesIncidentStatusEnum,
          classification:
            classification as InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
          type: type as InvestigationReportingControllerFindAllInjuriesIncidentTypeEnum,
          typeOfLoss:
            typeOfLoss as InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
          limit: Number(limit),
        }}
        isIncidentsLoading={isLoading || isRefetching}
        departments={departments}
        data={allInjuries}
        noDataMessage={noDataMessage}
      />
    </div>
  );
}
